export enum VisitType {
  Planned = 'planned',
  Unplanned = 'unplanned',
}

export enum VisitStatus {
  Completed = 'completed',
  Incomplete = 'incomplete',
}

export enum VisitPurpose {
  Regular = 'regular',
  Observation = 'observation',
}

export enum FeedbackGenerationState {
  Ready = 'ready',
  Loading = 'loading',
  Generated = 'generated',
  Error = 'error',
}

export interface IVisitPlan {
  id: string;
  fromDate: string;
  toDate: string;
  regionalManager?: number;
}

export interface ISchoolVisit {
  id: string;
  scheduledDate: string | null;
  visitDate: string | null;
  comments: string | null;
  status: VisitStatus;
  type: VisitType;
  visitPlan: string | null;
  school: number;
  coaches: number[];
}

export enum UserProfileType {
  Teacher = 'teacherprofile',
  Principal = 'principalprofile',
  Coach = 'coachprofile',
  RegionalManager = 'regionalmanagerprofile',
  AreaEducationOfficer = 'areaeducationofficerprofile',
  ProgramManager = 'programmanagerprofile',
  Administrator = 'administratorprofile',
}

export const UserProfileTypeLabel: Record<UserProfileType, string> = {
  [UserProfileType.Teacher]: 'Teacher',
  [UserProfileType.Principal]: 'Principal',
  [UserProfileType.Coach]: 'Coach',
  [UserProfileType.RegionalManager]: 'Regional Manager',
  [UserProfileType.AreaEducationOfficer]: 'Area Education Officer',
  [UserProfileType.ProgramManager]: 'Program Manager',
  [UserProfileType.Administrator]: 'Administrator',
};

export interface ITeacherVisit {
  id: string;
  scheduledDate: string | null;
  visitDate: string | null;
  comments: string | null;
  status: VisitStatus;
  schoolVisit: string | null;
  teacher: number;
  coach: number | null;
  coachName?: string | null;
  visitPurpose: VisitPurpose;
  school: number | null;
  gradeSubject: number | null;
  section: string | null;
  userProfileType?: UserProfileType | null;
  userProfileId?: number | null;
}

export interface IObservationTemplate {
  id: number;
  name: string;
  created: string; // ISO 8601 datetime from backend
}

export enum SectionType {
  Regular = 'regular',
  Summary = 'summary',
  Fidelity = 'fidelity',
  MiniAssessment = 'mini_assessment',
}

export enum ObservationStatus {
  InProgress = 'in_progress',
  PendingMultimedia = 'pending_multimedia', // Audio done, waiting for photos
  Completed = 'completed', // Ready for final sync and AI processing
}

export interface IObservationSection {
  id: number;
  template: number;
  title: string;
  sectionType: SectionType;
  isScored: boolean;
  order: number;
}

export interface IObservationQuestionGroup {
  id: number;
  section: number;
  title: string;
  order: number;
}

export enum ObservationQuestionType {
  Integer = 'integer',
  Text = 'text',
  Date = 'date',
  Time = 'time',
  SingleChoice = 'single_choice',
  MultiChoice = 'multi_choice',
}

export enum QuestionOptionScore {
  Yes = 'yes',
  Partial = 'partial',
  No = 'no',
  Ignore = 'ignore',
}

export enum QuestionPurpose {
  General = 'general',
  Fidelity = 'fidelity',
  MiniAssessment = 'mini_assessment',
}

export enum QuestionSource {
  Manual = 'manual',
  Automated = 'automated',
}

export interface IObservationQuestion {
  id: number;
  section: number;
  group: number | null;
  lessonPlan: number | null;
  prompt: string;
  type: ObservationQuestionType;
  required: boolean;
  isScored: boolean;
  purpose: QuestionPurpose;
  order: number;
  source: QuestionSource;
  subject: number | null;
  isLpFollowed?: boolean;
}

export interface IQuestionOption {
  id: number;
  question: number;
  label: string;
  value: string;
  scoreType: QuestionOptionScore;
  order: number;
}

export interface IObservation {
  id: string;
  template: number;
  coach: number | null;
  schoolClassSubject: number | null;
  numberOfBoys: number;
  numberOfGirls: number;
  lessonPlan: number | null;
  coreLessonPlan: number | null;
  bookChapter: number | null;
  observationDate: string;
  startTime: string;
  totalDuration: string | null;
  visit: string | null;
  status: ObservationStatus;
  feedback?: string | null;
  audioUrl?: string | null;
  userProfileType?: UserProfileType | null;
  userProfileId?: number | null;
  agreedWithFeedback?: boolean | null;
  teacherResponse?: string | null;
  imagesS3Urls?: string[] | null;
}

export interface StudentScoresField {
  studentName: string;
  score: number;
  totalScore: number;
  correctOptions: number[];
  wrongOptions: number[];
}

export interface IObservationAnswer {
  uuid: string;
  observation: string;
  question: number;
  studentNumber: number | null;
  studentName: string | null;
  answerText: string | null;
  singleChoiceOption: number | null;
  multiChoiceOptions: number[];
  wrongChoiceOptions: number[];
  isLpFollowed?: boolean | null;
  studentScores?: StudentScoresField | null;
}

export interface IRegion {
  id: number;
  name: string;
}

export interface ISchool {
  id: number;
  name: string;
  emis: string;
  region: number | null;
}

export interface ITeacherContext {
  id: number;
  teacherName: string;
  school: number;
}

export interface ISchoolClassContext {
  id: number;
  grade: number;
  section: string;
  shift: string;
  session: number;
}

export interface ISchoolClassSubjectContext {
  id: number;
  schoolClass: number;
  gradeSubject: number;
  teacher: number;
}

export interface ILessonPlanMini {
  id: number;
  topic: string;
  gradeSubject: number;
}

export interface IBookChapterMini {
  id: number;
  title: string;
  lps: number[];
  gradeSubject: number | null;
}

export interface ICoachObservation {
  id: string;
  template: number;
  coach: number;
  schoolClassSubject: number;
  numberOfBoys: number;
  numberOfGirls: number;
  lessonPlan: number;
  observationDate: string;
  startTime: string;
  totalDuration: string | null;
  visit: string | null;
  status: ObservationStatus;
  feedback: string | null;
}

export interface IObservationFeedback {
  id: string;
  visit?: string | null;
  observationDate: string;
  feedback: string;
  subjectName: string | null;
  className: string | null;
  lessonPlanName: string | null;
  userProfileType?: UserProfileType | null;
  agreedWithFeedback?: boolean | null;
  teacherResponse?: string | null;
  draftTeacherResponse?: string | null;
}
